import type { APIService } from '../interfaces/services';
import type { AttachmentMeta } from '../types';

const PDF_MAGIC = new Uint8Array([0x25, 0x50, 0x44, 0x46]); // %PDF
const SNIFF_BYTES = 512;
// application/vnd.ms-excel: on Windows, .csv files are associated with Excel so the
// browser reports this MIME type. Actual .xls binaries contain null bytes and are blocked
// by the null-byte check in detectCSV, so only text-like CSVs labeled as Excel pass.
const CSV_MIME_TYPES = new Set(['text/csv', 'application/vnd.ms-excel']);

function detectPDF(bytes: Uint8Array): boolean {
  return PDF_MAGIC.every((byte, i) => bytes[i] === byte);
}

function detectCSV(bytes: Uint8Array, mimeType: string): boolean {
  return CSV_MIME_TYPES.has(mimeType) && !bytes.includes(0x00);
}

export async function validateFileType(file: File): Promise<string | null> {
  const bytes = new Uint8Array(await file.slice(0, SNIFF_BYTES).arrayBuffer());
  if (detectPDF(bytes) || detectCSV(bytes, file.type)) return null;
  return 'File type not supported. Please upload a CSV or PDF file.';
}

type CreateAttachmentResponse = {
  upload_uuid: string;
  presigned_post: {
    url: string;
    fields: Record<string, string>;
  };
};

function uploadToS3WithProgress(
  url: string,
  formData: FormData,
  onProgress: (pct: number) => void,
  signal: AbortSignal,
): Promise<void> {
  const xhr = new XMLHttpRequest();
  let cleanup!: () => void;

  const promise = new Promise<void>((resolve, reject) => {
    const handleSignalAbort = () => xhr.abort();
    const handleProgress = (e: ProgressEvent) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    const handleLoad = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`S3 upload failed: ${xhr.status}`));
    };
    const handleError = () => reject(new Error('S3 upload failed'));
    const handleXhrAbort = () => reject(new DOMException('Aborted', 'AbortError'));

    signal.addEventListener('abort', handleSignalAbort);
    xhr.upload.addEventListener('progress', handleProgress);
    xhr.addEventListener('load', handleLoad);
    xhr.addEventListener('error', handleError);
    xhr.addEventListener('abort', handleXhrAbort);

    cleanup = () => {
      signal.removeEventListener('abort', handleSignalAbort);
      xhr.upload.removeEventListener('progress', handleProgress);
      xhr.removeEventListener('load', handleLoad);
      xhr.removeEventListener('error', handleError);
      xhr.removeEventListener('abort', handleXhrAbort);
    };

    xhr.open('POST', url);
    xhr.send(formData);
  });

  return promise.finally(cleanup);
}

export async function uploadAttachment(
  file: File,
  conversationId: string,
  apiService: APIService,
  onProgress: (pct: number) => void,
  signal: AbortSignal,
): Promise<AttachmentMeta> {
  const extension = file.name.includes('.') ? file.name.split('.').pop()?.toLowerCase() ?? '' : '';

  const response = await apiService.post<CreateAttachmentResponse>('/api/v1/assistant/attachments/', {
    file_name: file.name,
    extension,
    conversation_id: conversationId,
  });

  const { upload_uuid, presigned_post } = response;

  const formData = new FormData();
  Object.entries(presigned_post.fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append('file', file);

  await uploadToS3WithProgress(presigned_post.url, formData, onProgress, signal);

  return { uuid: upload_uuid, fileName: file.name };
}
